import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import uris from "../../../config/uris";

import { message, Spin, Tooltip, Upload } from "antd";
import { FaRegFilePdf, FaUpload, FaSyncAlt } from "react-icons/fa";
import { FiTrash } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import { IoChevronDown, IoChevronForward } from "react-icons/io5";
import { LuFilePlus2 } from "react-icons/lu";
import { TbFolderPlus } from "react-icons/tb";
import { GoTrash } from "react-icons/go";

import "./styles.scss";
import CustomModal from "../../../assets/component/modal";
import SpinLoader from "../../../assets/component/loader/loader";
import { GetFolderStructureAction, GetFolderStructureReset } from "../../../store/slices/custom-research/folder-structure";
import { DeleteFileAction, DeleteFileReset, UploadFileAction, UploadFileReset } from "../../../store/slices/custom-research/upload-file";
import { SyncNowAction, SyncNowReset } from "../../../store/slices/custom-research/SyncNow";

// Configurations
const maxUploadfile = 10;
const maxFileSize = 50; //MB
const conversionOfMBtoBytes = 1048576;
const actionItems = {
    file: "FILE",
    folder: "FOLDER",
    delete: "DELETE",
}

const ConfirmDeleteModal = ({ closeDeleteModal, confirmDelete, type }) => {
    const { isLoading: deleteLoading } = useSelector((state) => state.deleteFile)
    return (
        <div className="confirm-delete-modal-container">
            <div className="delete-modal-icon"><GoTrash /></div>
            <div className="confirm-delete-text">Are you sure you want to delete the {type.toLowerCase()}?</div>
            <div className="confirm-delete-buttons-section">
                {deleteLoading && (<div className="loading-container"><SpinLoader /></div>)}
                {!deleteLoading && (
                    <>
                        <div className="modal-button confirm-delete" onClick={() => confirmDelete()}>Delete</div>
                        <div className="modal-button cancel-delete" onClick={() => closeDeleteModal()}>Cancel</div>
                    </>
                )}

            </div>
        </div>
    )
}

const CreateFolderForm = ({ createFolderError, handleSubmitCreateFolderForm, handleCloseCreateFolder, folderNameInputRef, handleNameChange, folderName, isModalBody }) => {
    return (
        <div className={`file-details-form ${isModalBody ? "modal-form" : ""}`}>
            <div className="input-label">Folder Name</div>
            <div className={`input-container ${createFolderError?.isError ? "error-border" : ""}`}>
                <input
                    type="text"
                    placeholder="Name"
                    value={folderName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    ref={folderNameInputRef}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleSubmitCreateFolderForm();
                        }
                    }}
                />
            </div>
            {createFolderError?.isError && (
                <div className="form-error-message">{createFolderError?.message}!</div>
            )}
            <div className="form-submit-buttons">
                <div className="form-button create-folder" onClick={() => handleSubmitCreateFolderForm()}>Create</div>
                <div className="form-button cancel-create" onClick={() => handleCloseCreateFolder()}>Cancel</div>
            </div>
        </div>
    )
}

const FolderCard = ({
    folder,
    currentFolderName,
    handleNewFileUpload,
    handleNewFolder,
    handleDeleteContent,
    requiredFields,
    defaultExpand,
    lastUploadFolder,
    pageLayout,
    activeTab
}) => {

    const [showContents, setShowContents] = useState(defaultExpand);
    const [uploadFile, setUploadFile] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState(folder?.files || []);

    const { Dragger } = Upload;
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const location = useLocation()

    const { children: alreadyExistingfiles } = folder

    const formatFile = async (currentFile) => {
        const convertToBase64 = (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file?.originFileObj);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });
        };

        const base64File = await convertToBase64(currentFile);

        const structuredFile = {
            base64: base64File.split(";base64,")[1],
            format: currentFile?.type?.split("/")[1],
            file: currentFile.name,
            fileName: currentFile.name?.split(".")[0],
            title: currentFile.name,
            isLeaf: true
        };

        return structuredFile;
    };

    const handleUploadFiles = async (files) => {
        const fileSizeLimit = maxFileSize * conversionOfMBtoBytes;
        let structured_files = [...uploadedFiles]
        for (let i = 0; i < files.length; i++) {
            const single_file = files[i];
            if ((single_file.size <= fileSizeLimit)) {
                const formattedFile = await formatFile(single_file);
                const fileExistInFolder = alreadyExistingfiles?.some(oldFile => (oldFile.title.includes(formattedFile.fileName)));
                if (!fileExistInFolder)
                    structured_files.push(formattedFile)
                else
                    message.error("File already exists");
            }
        }
        setUploadedFiles(structured_files);
    };

    const handleDisplayFiles = () => {
        setShowContents(!showContents);
        setUploadFile(uploadFile && false);
    };

    const handleShowUpload = () => {
        setShowContents(true);
        setUploadFile(true);

        navigate(uris?.customDisclosureDetails, { state: { folderName: folder?.title } })
    };

    const createNewFolder = (name) => {
        setShowContents(true)
        handleNewFolder(folder?.title)

        navigate(uris?.customDisclosureDetails, { state: { folderName: folder?.title } })
    }

    const handleDeleteFolder = (folder) => {
        handleDeleteContent({ type: "Folder", path: folder?.value, title: "", isNewlyCreated: folder?.isNewlyCreated })
    }

    const handleDeleteFile = (file) => {
        handleDeleteContent({ type: "File", path: folder?.value, title: file?.title })
    }

    function debounce(func, delay) {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    }

    let errorDisplayed = false;
    const configureDragAndDrop = {
        name: 'file',
        multiple: true,
        beforeUpload: (file, fileList) => {
            if (fileList.length > 10) {
                if (!errorDisplayed) {
                    message.error('You can only upload up to 10 files at a time.');
                    errorDisplayed = true;
                }
                return Upload.LIST_IGNORE;
            }

            // Validate file type using MIME type
            const allowedMimeTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ];
            if (!allowedMimeTypes.includes(file.type)) {
                message.error(`${file.name} is not a supported file type.`);
                return Upload.LIST_IGNORE;
            }

            return true;
        },
        fileList: [], // Initial empty file list
        accept: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // Restrict file picker options (for UI)
        onChange: debounce(async (info) => {
            if (info?.file?.status === 'removed') {
                return;
            }
            const validFiles = info.fileList.slice(0, 10);
            if (validFiles.length <= 10) {
                await handleUploadFiles(validFiles);
            } else {
                message.error('You can only upload 10 files at a time at a time.');
            }

            errorDisplayed = false;
        }, 300)
    };

    useEffect(() => {
        dispatch(UploadFileReset())
        dispatch(DeleteFileReset())
    }, [])

    useEffect(() => {
        if (uploadedFiles?.length > 0) {
            handleNewFileUpload({ folderName: folder?.title, newFiles: uploadedFiles });
            setUploadFile(false);

            let payload = []
            uploadedFiles.map(uploadedFile => {
                payload.push({
                    folderPath: currentFolderName,
                    fileName: uploadedFile?.file,
                    base64: uploadedFile?.base64
                })
            })

            dispatch(UploadFileAction({ payload: payload }))
        }
    }, [uploadedFiles]);

    useEffect(() => {
        if (location?.state?.folderName === folder?.title) {
            setShowContents(true);
            setUploadFile(true);

            navigate(uris?.customDisclosureDetails, { state: { folderName: "" } })
        }
    }, [location?.state?.folderName])

    return (
        <div className={`folder-card-container ${showContents ? "show-contents" : ""}`}>
            <div className="folder-header-wrapper">
                <div className="name-section" onClick={() => handleDisplayFiles()}>
                    <div className="show-hide-icon">{showContents ? <IoChevronDown /> : <IoChevronForward />}</div>
                    <div className="folder-name">{folder?.title}</div>
                </div>
                {/* File */}
                {requiredFields?.includes(actionItems.file) && (
                    <Tooltip title="Add file" placement="top">
                        <div className="icon-section add-file-icon" onClick={() => handleShowUpload()}><LuFilePlus2 /></div>
                    </Tooltip>
                )}
                {/* Folder */}
                {requiredFields?.includes(actionItems.folder) && (
                    <Tooltip title="Create new folder" placement="top">
                        <div className="icon-section add-folder-icon" onClick={() => createNewFolder(folder.name)}><TbFolderPlus /></div>
                    </Tooltip>
                )}
                {/* Delete */}
                {requiredFields?.includes(actionItems?.delete) && activeTab === "Completed" && (
                    <Tooltip title="Delete folder" placement="top">
                        <div className="icon-section delete-folder-icon" onClick={() => handleDeleteFolder(folder)}>
                            <FiTrash />
                        </div>
                    </Tooltip>
                )}
            </div>

            {showContents && (
                <div className="folder-card-contents">
                    {uploadFile && (
                        <div className="upload-container">
                            <Dragger {...configureDragAndDrop}>
                                <div className="dragger-wrapper">
                                    <div className="upload-icon"><FaUpload /></div>
                                    <div className="upload-main-text">Click or drag file to upload</div>
                                    <div className="upload-description-text">Maximum file size to upload is {maxFileSize}MB</div>
                                </div>
                            </Dragger>
                        </div>
                    )}
                    <div className="files-list-container">
                        {folder?.children?.map((file, index) => {
                            if (file?.isLeaf) {
                                return (
                                    <div className="file-card" key={index}>
                                        <div className="file-icon"><FaRegFilePdf /></div>
                                        <div className="file-name">{file.title}</div>
                                        {/* Delete */}
                                        {requiredFields?.includes(actionItems.delete) && activeTab === "Completed" && (
                                            <Tooltip title="Delete File" placement="top">
                                                <div className="file-icon trash-icon" onClick={() => handleDeleteFile(file)}><FiTrash /></div>
                                            </Tooltip>
                                        )}
                                    </div>
                                );
                            } else {
                                const shouldExpand = (file?.title === lastUploadFolder) || (file?.children?.some((child) => child?.title === lastUploadFolder))
                                return (
                                    <FolderCard
                                        key={index}
                                        folder={file}
                                        currentFolderName={`${currentFolderName}/${file.title}`}
                                        handleNewFileUpload={handleNewFileUpload}
                                        handleNewFolder={handleNewFolder}
                                        handleDeleteContent={handleDeleteContent}
                                        requiredFields={requiredFields}
                                        defaultExpand={shouldExpand}
                                        lastUploadFolder={lastUploadFolder}
                                        pageLayout={pageLayout}
                                        activeTab={activeTab}
                                    />
                                );
                            }
                        })}
                        {(folder?.children?.length === 0) && (
                            <div className="file-card"> No Files</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const FileSaver = ({ showCreateFolder, sectionTitle, requiredFields, pageLayout, activeTab, setActiveTab, tabOptions, setUnsyncedFilesCount }) => {
    const [createFolderError, setCreateFolderError] = useState({ isError: false, message: "" });
    const [newFoldersList, setNewFoldersList] = useState([])
    const [showFileForm, setShowFileForm] = useState(false);
    const [folderName, setFolderName] = useState("");
    const [currentFolder, setCurrentFolder] = useState(null);
    const [lastUploadFolder, setLastUploadFolder] = useState(null);
    const [deleteContent, setDeleteContent] = useState({ show: false, type: "", path: "", title: "" })

    const folderNameInputRef = useRef(null);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();

    const { error: uploadError, success: uploadSuccess, isLoading: uploadLoading, data: uploadData } = useSelector((state) => state.uploadFile)
    const { error: deleteError, success: deleteSuccess, data: deleteFileData } = useSelector((state) => state.deleteFile)
    const { data: uploadFolders, isLoading: fetchFoldersLoading } = useSelector((state) => state.fetchFolders)
    const { data: syncNowData } = useSelector((state) => state.syncNow)

    const resetForm = () => {
        setFolderName("");
        setCreateFolderError({ isError: false, message: "" })
    };

    const handleNameChange = (name) => {
        setFolderName(name)
        setCreateFolderError({ isError: false, message: "" })
    }

    const handleCreateFolder = () => {
        setCurrentFolder(null); // Reset current folder to ensure top-level creation
        setShowFileForm(true);
        resetForm();
    };

    const redirectFolderCreation = () => {
        navigate(uris?.customDisclosureDetails, { state: { createFolderRedirect: true } })
    }

    // const handleSyncNow = () => {
    //     dispatch(SyncNowAction())
    // }

    const handleCloseCreateFolder = () => {
        setShowFileForm(false);
        resetForm();
    };

    const addSubFolder = (folders, folderName, newFolder) => {
        return folders.map(folder => {
            if (folder.title === folderName) {
                return {
                    ...folder,
                    children: [...folder.children, newFolder]
                };
            } else if (folder?.children?.length > 0) {
                return {
                    ...folder,
                    children: addSubFolder(folder.children, folderName, newFolder)
                };
            }
            return folder;
        });
    };

    const getNonLeafTitles = () => {
        const flattenAndFilter = (nodes) => {
            return nodes.flatMap(node =>
                node.isLeaf ? [] : [node.title, ...(node.children ? flattenAndFilter(node.children) : [])]
            );
        };

        return flattenAndFilter(newFoldersList);
    }

    const handleSubmitCreateFolderForm = () => {
        if (folderName) {
            const newFolder = {
                title: folderName,
                value: folderName,
                isNewlyCreated: true,
                children: []
            };

            const foldersList = getNonLeafTitles();
            const isValidName = !foldersList.includes(folderName)

            if (isValidName) {
                if (currentFolder) {
                    const updatedFoldersData = addSubFolder(newFoldersList, currentFolder, newFolder);
                    setNewFoldersList(updatedFoldersData);
                } else {
                    setNewFoldersList([...newFoldersList, newFolder]);
                }

                setCreateFolderError({ isError: false, message: "" })
                handleCloseCreateFolder();
            } else {
                setCreateFolderError({ isError: true, message: "Name already exists." })
            }

        }
    };

    const updateFolderFiles = (folders, folderName, newFiles) => {
        return folders?.map(folder => {
            if (folder?.title === folderName) {
                return {
                    ...folder,
                    children: [...folder?.children, ...newFiles]
                };
            } else if (folder?.children?.length > 0) {
                return {
                    ...folder,
                    children: updateFolderFiles(folder.children, folderName, newFiles)
                };
            }
            return folder;
        });
    };

    const handleNewFileUpload = ({ folderName, newFiles }) => {
        const updatedFoldersData = updateFolderFiles(newFoldersList, folderName, newFiles);
        setLastUploadFolder(folderName)
        setNewFoldersList(updatedFoldersData);
    };

    const handleNewFolder = (parentFolderName) => {
        setCurrentFolder(parentFolderName);
        setShowFileForm(true);
    };

    const handleDeleteContent = ({ type, path, title, isNewlyCreated }) => {
        setDeleteContent({ show: true, type, path, title, isNewlyCreated })
    };

    const handleCloseDeleteModal = () => {
        setDeleteContent({ show: false, type: "", path: "" })
    }

    const localDeleteFolder = (folders, deleteContent) => {
        return folders
            .map(folder => {
                if (folder.children) {
                    const updatedChildren = localDeleteFolder(folder.children, deleteContent);
                    return { ...folder, children: updatedChildren };
                }
                return folder;
            })
            .filter(folder => folder.value !== deleteContent.path); // Remove the matching folder
    };

    const handleConfirmDelete = () => {
        if (deleteContent?.isNewlyCreated) {
            const updatedList = localDeleteFolder(newFoldersList, deleteContent);
            setNewFoldersList(updatedList);

            message.success("Folder deleted successfully!")
            handleCloseDeleteModal()
        } else if (deleteContent?.show) {
            dispatch(DeleteFileReset());

            let payload = {
                folderPath: [],
                filePath: []
            }

            if (deleteContent?.type === "Folder") {
                payload = {
                    ...payload,
                    folderPath: [deleteContent?.path]
                }
            }

            if (deleteContent?.type === "File") {
                payload = {
                    ...payload,
                    filePath: [`${deleteContent?.path}/${deleteContent?.title}`]
                }
            }

            dispatch(DeleteFileAction(payload))
        } else {
            message.error("An error occurred! Please refresh and try again.")
        }
    }

    const isLastUploadFolderInChildren = (folder, lastUploadFolder) => {
        if (!folder?.children) return false;

        // Check if any child's title matches
        return folder.children.some(child =>
            child?.title === lastUploadFolder ||
            isLastUploadFolderInChildren(child, lastUploadFolder)
        );
    };

    const resetStates = () => {
        // RESET STATE
        dispatch(GetFolderStructureReset())
        dispatch(UploadFileReset())
        dispatch(DeleteFileReset())

        setDeleteContent({ show: false, type: "", path: "", title: "" })
        setNewFoldersList([])

        dispatch(GetFolderStructureAction({ sync_status: activeTab || "Completed" }))
    }

    useEffect(() => {
        if (activeTab)
            resetStates()
    }, [activeTab])

    useEffect(() => {
        if (showFileForm) {
            if (folderNameInputRef.current) {
                folderNameInputRef.current.focus();
            }
        }
    }, [showFileForm]);

    useEffect(() => {
        if (uploadError?.message) {
            message.error(uploadError?.message)
            resetStates();
        }
    }, [uploadError?.message])

    useEffect(() => {
        if (uploadSuccess) {
            messageApi.open({
                type: 'success',
                content: uploadData?.message,
                duration: 5,
            });
            setActiveTab(tabOptions?.tabs[1]?.payloadType)
        }
    }, [uploadSuccess])

    useEffect(() => {
        if (uploadFolders?.files) {
            setNewFoldersList(uploadFolders?.files)
            dispatch(GetFolderStructureReset())
        }
        if (uploadFolders?.pendingSyncFilesCount && setUnsyncedFilesCount) setUnsyncedFilesCount(uploadFolders?.pendingSyncFilesCount)
    }, [uploadFolders, setNewFoldersList])

    useEffect(() => {
        if (deleteError?.message) {
            message.error(deleteError?.message)
            resetStates()
        }
    }, [deleteError?.message])

    useEffect(() => {
        if (deleteSuccess) {
            message.success(deleteFileData?.message)
            resetStates()
        }
    }, [deleteSuccess])

    useEffect(() => {
        if (location?.state?.folderName) {
            setLastUploadFolder(location?.state?.folderName)
        }
    }, [location?.state?.folderName])

    useEffect(() => {
        if (location?.state?.createFolderRedirect) {
            setCurrentFolder(null);
            setShowFileForm(true);
            resetForm();

            location.state = {}
        }
    }, [location?.state?.createFolderRedirect])

    useEffect(() => {
        if (syncNowData?.message) {
            messageApi.open({
                type: 'success',
                content: syncNowData?.message,
                duration: 5,
            });
            dispatch(SyncNowReset())
        }
    }, [syncNowData])


    return (
        <div className="file-saver-container">
            {contextHolder}
            {uploadLoading && (
                <div className="card-loader-container">
                    <Spin size="large" />
                </div>
            )}

            {sectionTitle && (
                <div className="container-title-section">
                    {sectionTitle}
                </div>
            )}

            {pageLayout && (activeTab === "Completed") && (
                <div className="actions-section">
                    <div className="create-folder-button" onClick={() => handleCreateFolder()}>
                        <IoMdAdd className="button-icon" />
                        <span className="button-text">Create Folder</span>
                    </div>
                </div>
            )}

            <div className="folders-container">
                {fetchFoldersLoading ?
                    <div className="folders-container-align-center"><SpinLoader /></div>
                    : newFoldersList?.length > 0 ? newFoldersList?.map((folder) => {
                        const shouldExpand =
                            folder?.title === lastUploadFolder ||
                            isLastUploadFolderInChildren(folder, lastUploadFolder);

                        return (
                            <FolderCard
                                key={folder?.title}
                                folder={folder}
                                currentFolderName={folder?.title}
                                handleNewFileUpload={handleNewFileUpload}
                                handleNewFolder={handleNewFolder}
                                handleDeleteContent={handleDeleteContent}
                                requiredFields={requiredFields}
                                lastUploadFolder={lastUploadFolder}
                                defaultExpand={shouldExpand}
                                pageLayout={pageLayout}
                                activeTab={activeTab}
                            />
                        );
                    })
                        :
                        <div className="folders-container-align-center">{activeTab === "Pending" ? "All files are synced" : "No files found"}</div>
                }
            </div>

            {/* Page Layout option for the Full screen options & Modal Form */}
            {!pageLayout && (
                <div className="create-folder-section">
                    {showFileForm && (
                        <CreateFolderForm
                            folderName={folderName}
                            createFolderError={createFolderError}
                            handleNameChange={handleNameChange}
                            folderNameInputRef={folderNameInputRef}
                            handleCloseCreateFolder={handleCloseCreateFolder}
                            handleSubmitCreateFolderForm={handleSubmitCreateFolderForm}
                        />
                    )}
                    {(showCreateFolder && !showFileForm) && (
                        <div className="create-folder-button" onClick={() => redirectFolderCreation()}>
                            <IoMdAdd /> Create Folder
                        </div>
                    )}
                </div>
            )}

            {(pageLayout && showFileForm) && (
                <CustomModal modalTitle="Create Folder" showCloseIcon handleClose={handleCloseCreateFolder}>
                    <CreateFolderForm
                        folderName={folderName}
                        createFolderError={createFolderError}
                        handleNameChange={handleNameChange}
                        folderNameInputRef={folderNameInputRef}
                        handleCloseCreateFolder={handleCloseCreateFolder}
                        handleSubmitCreateFolderForm={handleSubmitCreateFolderForm}
                        isModalBody={pageLayout}
                    />
                </CustomModal>

            )}

            {deleteContent?.show && (
                <CustomModal
                    showCloseIcon
                    handleClose={handleCloseDeleteModal}
                >
                    <ConfirmDeleteModal
                        closeDeleteModal={handleCloseDeleteModal}
                        confirmDelete={handleConfirmDelete}
                        type={deleteContent?.type}
                    />
                </CustomModal>
            )}
        </div>
    );
};

export default FileSaver;
